import {
  type OpenChangeReason,
  autoUpdate,
  flip,
  hide,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import { interactionResponse } from '@zillow/yield-callback';
import { type SyntheticEvent, useCallback, useId, useMemo, useRef, useState } from 'react';

import { useReducedMotion } from '../../../hooks/use-reduced-motion';
import type { DropdownRootPropsInterface } from '../types/dropdown-root.types';
import type {
  DropdownFloatingContextInterface,
  UseDropdownReturnInterface,
} from '../types/use-dropdown.types';

export function useDropdown(props: Partial<DropdownRootPropsInterface>): {
  dropdown: UseDropdownReturnInterface;
  floating: DropdownFloatingContextInterface;
} {
  const {
    autoMaxHeight = false,
    defaultOpen,
    offset: offsetValue = 8,
    open: controlledOpen,
    onOpenChange: setControlledOpen,
    overflowPadding = 8,
    placement = 'bottom-start',
    shouldCloseOnViewportLeave = false,
    shouldAwaitInteractionResponse = true,
    useClickProps,
    useDismissProps,
    useRoleProps,
  } = props;

  /**
   * Tracks whether the current open came from activating the trigger so focus can remain on it,
   * matching Menu's disclosure pattern. `useClick` reports reason 'click' for both pointer and
   * keyboard (Enter/Space) activation, so this is true for either input method. Named for parity
   * with `useMenu`.
   */
  const openedByPointerRef = useRef<boolean>(false);

  /**
   * Accessibility props and handlers to connect header with body
   */
  const contentId = useId();
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();

  /**
   * Open state management (controlled or uncontrolled)
   */
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = 'open' in props;
  const open = isControlled ? controlledOpen : uncontrolledOpen || false;

  /**
   * Floating UI setup for modal state management
   */
  const { context, refs, floatingStyles, middlewareData } = useFloating({
    placement,

    open,

    onOpenChange: async (state, event, reason) => {
      openedByPointerRef.current = state && reason === 'click';

      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      updateOpenState(state, event, reason);
    },

    whileElementsMounted: autoUpdate,

    /**
     * Middleware plugins have to be in certain order.
     * See: https://floating-ui.com/docs/useFloating#middleware
     */
    middleware: [
      // Translates the floating element along the specified axes
      offset(offsetValue),

      // Automatically set max height based on available space
      ...(autoMaxHeight
        ? [
            size({
              apply({ availableHeight, elements }) {
                // Get number value for offset
                const offsetMainAxis =
                  typeof offsetValue === 'number'
                    ? offsetValue
                    : typeof offsetValue === 'function'
                      ? 8
                      : (offsetValue.mainAxis ?? 8);

                // Convert available space to pixels and add offset so it doesn't touch the viewport
                const value = `${Math.max(0, availableHeight - offsetMainAxis)}px`;

                // Find Dropdown.Content within Dropdown.ContentPositioner
                const dropdownContent = elements.floating.querySelector(
                  '[data-c11n-component="Dropdown.Content"]',
                ) as HTMLElement | null;

                // Apply max height and overflow styles
                if (dropdownContent) {
                  dropdownContent.style.maxHeight = value;
                  dropdownContent.style.overflow = 'auto';
                }
              },
            }),
          ]
        : []),

      // Changes the placement of the floating element to keep it in view
      flip({
        fallbackPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'],
        padding: overflowPadding,
      }),

      // A data provider that allows you to hide the floating element
      ...(shouldCloseOnViewportLeave ? [hide({ padding: overflowPadding })] : []),
    ],
  });

  /**
   * Floating UI interaction hooks for accessibility and behavior
   * Sets up click and dismiss event handlers, and ARIA attributes based on role
   */
  const click = useClick(context, {
    enabled: true,
    event: 'mousedown',
    ...useClickProps,
  });

  const dismiss = useDismiss(context, {
    enabled: true,
    outsidePress: (event) =>
      !(event.target as HTMLElement).closest('[data-c11n-component="Dropdown"]'),
    ...useDismissProps,
  });

  const role = useRole(context, {
    ...useRoleProps,
  });

  const { getFloatingProps, getReferenceProps } = useInteractions([click, dismiss, role]);

  /**
   * Motion helpers and styles
   */
  const reducedMotion = useReducedMotion();

  const { isMounted, styles: floatingTransitionStyles } = useTransitionStyles(context, {
    duration: reducedMotion ? 0 : 250,
    initial: {
      opacity: 0,
      transform: 'scale(0.975)',
    },
  });

  /**
   * Handles controlled or uncontrolled "open" state.
   *
   * @param newValue - New state
   * @param event - Event object
   * @param reason - Reason for state change
   */
  const updateOpenState = useCallback(
    (newValue: boolean, event?: Event, reason?: OpenChangeReason) => {
      if (isControlled) {
        setControlledOpen?.(newValue, event, reason);
      } else {
        setUncontrolledOpen(newValue);
        setControlledOpen?.(newValue, event, reason);
      }
    },
    [isControlled, setControlledOpen],
  );

  /**
   * Handles `Dropdown.CloseButton` click actions.
   *
   * @param event - Event object
   */
  const onClickClose = useCallback(
    (event: SyntheticEvent) => {
      updateOpenState(false, event as unknown as Event, 'click');
    },
    [updateOpenState],
  );

  const dropdown = useMemo(
    () => ({
      // Internal focus management
      openedByPointerRef,

      // Open/mount state and close handlers
      open,
      isMounted,
      onClickClose,

      // Trigger and reference refs
      refs,

      // Trigger and reference props
      getReferenceProps,

      // Styles
      floatingStyles,
      floatingTransitionStyles,

      // Accessibility props and handlers
      contentId,
      labelId,
      setLabelId,
      descriptionId,
      setDescriptionId,

      middlewareData,
    }),
    [
      open,
      isMounted,
      onClickClose,
      openedByPointerRef,
      refs,
      getReferenceProps,
      floatingStyles,
      floatingTransitionStyles,
      contentId,
      labelId,
      descriptionId,
      middlewareData,
    ],
  );

  /*
   * Floating-ui-bridged data. Keep it on a separate context so only
   * Dropdown.Content pays for fresh Floating UI values.
   */
  const floating = { context, getFloatingProps };

  return { dropdown, floating };
}
